import type { ReactNode } from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { PageSection } from '../PageLayout'
import { SectionHeadline } from '@/components/SectionHeadline'
import { Card, CardContent } from '@/components/Card'
import { Avatar, type AvatarSubject } from '@/components/Avatar'
import { formatMoney } from '@/utils/money-util'
import { dateFormatters } from '@/utils/date-util'
import { Icon } from '@/components/Icon'
import type { JobBillingDetailsViewModel } from '@/features/jobs/details/JobBillingDetailsViewModel'
import logoUrl from '@/assets/misc/img_taxfyle-symbol.svg'
import styles from './BillingSummarySection.module.css'

interface BillingSummarySectionProps {
  headline: string
  children?: ReactNode
  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * The BillingSummarySection
 */
export function BillingSummarySection({
  headline,
  children,
  testId,
}: BillingSummarySectionProps) {
  return (
    <PageSection>
      <SectionHeadline>{headline}</SectionHeadline>
      <Card testId={testId}>
        <CardContent>
          <div className={styles.content}>{children}</div>
        </CardContent>
      </Card>
    </PageSection>
  )
}

interface ProfileSectionProps {
  proSubject: AvatarSubject
  workspaceSubject: AvatarSubject
  /**
   * Test ID attribute.
   */
  testId?: string
  /**
   * Test ID attribute for the pro name.
   */
  proNameTestId?: string
}

/**
 * The ProfileSection
 */
export function ProfileSection({
  proSubject,
  workspaceSubject,
  testId,
  proNameTestId,
}: ProfileSectionProps) {
  return (
    <div data-test={testId} className={styles.profileSection}>
      <div className={styles.avatar}>
        <div className={styles.avatarItem}>
          <Avatar subject={proSubject}></Avatar>
        </div>

        <div className={styles.avatarItem}>
          <Avatar size={24} subject={workspaceSubject}></Avatar>
        </div>

        <div className="pl-xl">
          <div
            data-test={proNameTestId}
            className="text-headline-lg mb-sm text-left"
          >
            {proSubject.displayName}
          </div>
          <div className="text-body-base mb-2xl text-left">
            Your assigned pro
          </div>
        </div>
      </div>
    </div>
  )
}

interface PendingProSectionProps {
  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Thea PendingProSection
 */
export function PendingProSection({ testId }: PendingProSectionProps) {
  return (
    <div data-test={testId} className={styles.profileSection}>
      <div className={styles.avatar}>
        <div className={styles.avatarItem}>
          <Icon name="search" variant="surface-background" />
        </div>
        <div className="pl-xl">
          <div className="text-headline-lg mb-sm text-left">
            Pending Pro pickup
          </div>
          <div className="text-body-base mb-2xl text-left">
            Matching in progress...
          </div>
        </div>
      </div>
    </div>
  )
}

interface OrderSummarySectionProps {
  date: Date
  children?: ReactNode
  /**
   * Test ID attribute.
   */
  testId?: string
  /**
   * Test ID attribute for the submitted date.
   */
  submittedDateTestId?: string
}

/**
 * The OrderSummarySection
 */
export function OrderSummarySection({
  date,
  children,
  testId,
  submittedDateTestId,
}: OrderSummarySectionProps) {
  return (
    <div data-test={testId} className="flex flex-col gap-lg">
      <div className="flex flex-col gap-sm text-left">
        <div className={styles.headline}>Submitted</div>
        <div data-test={submittedDateTestId} className={styles.item}>
          {dateFormatters.dateTimeDefault.format(date)}
        </div>
      </div>
      <div className={styles.orderSummary}>
        <div className={styles.headline}>Order summary</div>
        {children}
      </div>
    </div>
  )
}

interface OrderSummaryLineItemProps {
  text: string
  testId?: string
}

/**
 * The OrderSummaryLineItem
 */
export function OrderSummaryLineItem({
  text,
  testId,
}: OrderSummaryLineItemProps) {
  return (
    <div data-test={testId} className={styles.item}>
      {text}
    </div>
  )
}

interface OrderPriceSectionProps {
  primaryAmount: number
  amendedAmount: number
  couponCode: string
  couponAmount: number
  total: number
  testId?: string
  subtotalTestId?: string
  amendmentsTestId?: string
  discountTestId?: string
  totalTestId?: string
}

/**
 * The OrderPriceSection
 */
export function OrderPriceSection({
  primaryAmount,
  amendedAmount,
  couponCode,
  couponAmount,
  total,
  testId,
  subtotalTestId,
  amendmentsTestId,
  discountTestId,
  totalTestId,
}: OrderPriceSectionProps) {
  return (
    <div data-test={testId} className={styles.priceSummary}>
      <div className={cx(styles.item)}>
        <span>Subtotal</span>
        <span data-test={subtotalTestId}>{formatMoney(primaryAmount)}</span>
      </div>

      {amendedAmount > 0 && (
        <div className={styles.item}>
          <span>Amendments</span>
          <span data-test={amendmentsTestId}>{formatMoney(amendedAmount)}</span>
        </div>
      )}

      {couponAmount > 0 && (
        <div className={styles.item}>
          <span className="flex gap-sm items-center">
            Discount <Chip text={couponCode} />
          </span>
          <span data-test={discountTestId}>-{formatMoney(couponAmount)}</span>
        </div>
      )}

      <div className={styles.item}>
        <span>Total</span>
        <span data-test={totalTestId}>{formatMoney(total)}</span>
      </div>
    </div>
  )
}

interface ChipProps {
  text: string
  testId?: string
}

/**
 * The Chip
 */
export function Chip({ text, testId }: ChipProps) {
  return (
    <div data-test={testId} className={styles.chip}>
      {text}
    </div>
  )
}

interface BillingSummarySectionContainerProps {
  vm: JobBillingDetailsViewModel
}

/**
 * The BillingSummarySectionContainer
 */
export const BillingSummarySectionContainer = observer(
  function BillingSummarySectionContainer({
    vm,
  }: BillingSummarySectionContainerProps) {
    return (
      <BillingSummarySection headline="Your summary">
        {vm.job.championPro?.user ? (
          <ProfileSection
            proSubject={{
              displayName: vm.job.championPro?.user.displayName,
              avatarUrl: vm.job.championPro?.user.avatarUrl,
            }}
            workspaceSubject={{
              displayName: 'Taxfyle',
              avatarUrl: logoUrl,
            }}
          />
        ) : (
          <PendingProSection />
        )}

        <OrderSummarySection date={vm.job.submittedAt}>
          {vm.job.jobScopeItems.map((x, idx) => (
            <OrderSummaryLineItem key={idx} text={x.name} />
          ))}
        </OrderSummarySection>

        <OrderPriceSection
          primaryAmount={vm.job.primaryAmount}
          amendedAmount={vm.job.amendedAmount}
          couponCode={vm.job.couponCode}
          couponAmount={vm.job.couponAmount}
          total={vm.job.total}
        />
      </BillingSummarySection>
    )
  },
)
